// =============================
// FILE: src/components/QuoteForm.jsx
// =============================
import React, { useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";

/* Budget slider config */
const BUDGET_MIN = 1000;
const BUDGET_MAX = 100000;
const BUDGET_STEP = 500;

/** Product keys (must exist at: common.applications.catalog.*) */
const PRODUCT_OPTIONS = [
  { key: "adminstarter" }, // Admin Dashboard Starter
  { key: "appointments" }, // Appointments App
  { key: "management" }, // SmallBiz Manager
  { key: "presentation" }, // Presentation Website
  { key: "learning" }, // Learning Platform
  { key: "testing" }, // Testing (Manual & Automation)
];

/** Feature keys (translated at: common.contact.features.*) */
const FEATURES = [
  "auth",
  "payments",
  "admin",
  "chat",
  "analytics",
  "multilang",
  "subscriptions",
  "uploads",
  "integrations",
];

/** Stage & Styles (translated at: common.contact.stages.*, common.contact.styles.*) */
const STAGES = ["idea", "prototype", "mvp", "live", "migration"];
const STYLE_PRESETS = [
  "modern_minimal",
  "playful",
  "corporate",
  "dark_first",
  "colorful",
  "techy",
];

/** Product → recommended features */
const PRODUCT_FEATURE_TAGS = {
  adminstarter: [
    "auth",
    "admin",
    "analytics",
    "uploads",
    "integrations",
    "multilang",
  ],
  appointments: [
    "auth",
    "payments",
    "analytics",
    "integrations",
    "multilang",
    "chat",
    "admin",
  ],
  management: [
    "auth",
    "admin",
    "chat",
    "analytics",
    "uploads",
    "integrations",
    "multilang",
  ],
  presentation: ["analytics", "multilang", "uploads", "integrations"],
  learning: [
    "auth",
    "subscriptions",
    "analytics",
    "uploads",
    "integrations",
    "multilang",
  ],
  testing: ["analytics", "integrations"],
};

function classNames(...a) {
  return a.filter(Boolean).join(" ");
}

function formatMoneyEUR(n) {
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(n || 0));
  } catch {
    return `€${n}`;
  }
}

/** Simple tag input reused for Integrations/Inspiration etc. */
function TagInput({ label, values, setValues, placeholder, max = 6, name }) {
  const [input, setInput] = useState("");

  function add(val) {
    const v = val.trim();
    if (!v) return;
    if (values.includes(v)) return;
    if (values.length >= max) return;
    setValues([...values, v]);
    setInput("");
  }
  function onKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      add(input);
    } else if (e.key === "Backspace" && !input && values.length) {
      setValues(values.slice(0, -1));
    }
  }

  return (
    <div className="space-y-2">
      {label && (
        <label
          className="block text-sm font-medium text-gray-200"
          htmlFor={name}
        >
          {label}
        </label>
      )}
      <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-gray-700 bg-gray-900/40 px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500">
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-indigo-500/10 px-2.5 py-1 text-xs text-indigo-300"
          >
            {v}
            <button
              type="button"
              onClick={() => setValues(values.filter((x) => x !== v))}
              className="rounded-full p-0.5 hover:bg-indigo-500/20"
              aria-label={`Remove ${v}`}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={name}
          name={name}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder || "Type and press Enter"}
          className="min-w-[140px] flex-1 bg-transparent py-1.5 text-sm text-gray-100 placeholder:text-gray-500 focus:outline-none"
        />
        <input type="hidden" name={`${name}[]`} value={values.join(", ")} />
      </div>
      <p className="text-xs text-gray-500">
        Up to {max} items. Press Enter to add.
      </p>
    </div>
  );
}

export default function QuoteForm() {
  // 1) All contact strings (common.contact.*)
  const { t: tContact, i18n } = useTranslation("common", {
    keyPrefix: "contact",
  });
  // 2) Product catalog strings (common.applications.catalog.*)
  const { t: tCommon } = useTranslation("common");

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  // Keep projectTypes for backend; UI uses single product
  const [data, setData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    country: "",
    contactMethod: "Email",
    nda: false,

    product: "",
    projectTypes: [],

    stage: STAGES[0],
    goals: "",
    features: [],
    integrations: [],
    style: STYLE_PRESETS[0],
    inspiration: [],
    colorNotes: "",
    budget: 10000,
    unsureBudget: false,
    startWhen: "within_1_2_weeks",
    deadline: "",
    notes: "",
    website: "", // honeypot
  });

  const progress = useMemo(() => ((step + 1) / 3) * 100, [step]);

  // ---- product label helpers (MUST use tCommon without keyPrefix) ----
  const productLabel = (k) => tCommon(`applications.catalog.${k}.name`);
  const productBlurb = (k) => tCommon(`applications.catalog.${k}.blurb`);

  function next() {
    setStep((s) => Math.min(2, s + 1));
  }
  function prev() {
    setStep((s) => Math.max(0, s - 1));
  }
  function update(k, v) {
    setData((d) => ({ ...d, [k]: v }));
  }

  /** Resets features to the recommended set for the current product */
  function resetToRecommended(key) {
    const rec = PRODUCT_FEATURE_TAGS[key] || [];
    setData((d) => ({ ...d, features: [...rec] }));
  }

  /** When user picks a product:
   *  - sync projectTypes
   *  - ALWAYS reset features to this product's recommended list
   *    (prevents carry-over like auth/admin when switching to Presentation)
   */
  function selectProduct(key) {
    setData((d) => ({
      ...d,
      product: key,
      projectTypes: key ? [key] : [],
      features: [...(PRODUCT_FEATURE_TAGS[key] || [])],
    }));
  }

  function clearAllFeatures() {
    setData((d) => ({ ...d, features: [] }));
  }

  function toggleList(k, v) {
    setData((d) => ({
      ...d,
      [k]: d[k].includes(v) ? d[k].filter((x) => x !== v) : [...d[k], v],
    }));
  }

  function validateStep() {
    setError("");
    if (step === 0) {
      if (!data.name.trim()) return setError(tContact("errors.name"));
      const emailOk = /[^@\s]+@[^@\s]+\.[^@\s]+/.test(data.email);
      if (!emailOk) return setError(tContact("errors.email"));
      return true;
    }
    if (step === 1) {
      if (!data.product) return setError(tContact("errors.projectType"));
      if (!data.goals.trim()) return setError(tContact("errors.goals"));
      return true;
    }
    return true;
  }

  async function onSubmit(e) {
    e.preventDefault();
    setError("");
    if (!validateStep()) return;
    if (data.website) {
      setError("Blocked (spam suspected).");
      return;
    }

    const pretty = {
      lang: i18n.language,
      product_label: data.product ? productLabel(data.product) : "-",
      stage_label: data.stage ? tContact(`stages.${data.stage}`) : "-",
      style_label: data.style ? tContact(`styles.${data.style}`) : "-",
      features_labels: Array.isArray(data.features)
        ? data.features.map((f) => tContact(`features.${f}`))
        : [],
      start_label: data.startWhen ? tContact(`starts.${data.startWhen}`) : "-",
      budget_label: data.unsureBudget
        ? tContact("labels.not_sure")
        : formatMoneyEUR(data.budget),
    };

    setBusy(true);
    const payload = {
      ...data,
      projectTypes: data.product ? [data.product] : data.projectTypes,
      submittedAt: new Date().toISOString(),
      to: "andreitechro@gmail.com",
      source: "AndreiTech.ro",
      pretty, // <— include human-readable labels
      product_label: pretty.product_label, // (top-level mirrors for convenience)
      stage_label: pretty.stage_label,
      style_label: pretty.style_label,
      start_label: pretty.start_label,
    };

    try {
      const FORMSPREE_ID = process.env.REACT_APP_FORMSPREE_ID;
      if (FORMSPREE_ID) {
        const res = await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Formspree rejected the submission.");
      } else {
        const endpoint =
          process.env.REACT_APP_CONTACT_ENDPOINT || "/api/contact";
        const res = await fetch(endpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error("Server rejected the submission.");
      }
      setDone(true);
    } catch (err) {
      console.error(err);
      setError(tContact("errors.submit"));
    } finally {
      setBusy(false);
    }
  }

  const recommendedFeatures = data.product
    ? PRODUCT_FEATURE_TAGS[data.product] || []
    : [];

  if (done) {
    return (
      <div className="mx-auto max-w-2xl rounded-3xl border border-emerald-700/40 bg-emerald-900/20 p-8 text-center shadow-2xl">
        <div className="mx-auto mb-4 grid h-12 w-12 place-items-center rounded-full bg-emerald-500/20">
          ✅
        </div>
        <h2 className="text-2xl font-semibold text-white">
          {tContact("success.title")}
        </h2>
        <p className="mt-2 text-sm text-emerald-200">
          <Trans
            ns="common"
            i18nKey="contact.success.desc"
            values={{ email: data.email }}
            components={{ b: <b /> }}
          />
        </p>

        {/* Center the button horizontally */}
        <div className="mt-6 flex justify-center">
          <a
            href="/"
            className="inline-flex items-center rounded-xl bg-emerald-500 px-4 py-2 text-sm font-medium text-emerald-950 hover:bg-emerald-400"
          >
            {tContact("success.back_home")}
          </a>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="mx-auto max-w-3xl">
      {/* Progress */}
      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between text-xs text-gray-400">
          <span>{tContact("progress.step", { n: step + 1, total: 3 })}</span>
          <span>{Math.round(progress)}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-gray-800">
          <div
            className="h-full bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500 transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="rounded-3xl border border-gray-800 bg-gray-900/40 p-6 shadow-2xl backdrop-blur">
        {/* STEP 0: About you */}
        {step === 0 && (
          <div className="space-y-6">
            <header className="space-y-1">
              <h2 className="text-xl font-semibold text-white">
                {tContact("about.title")}
              </h2>
              <p className="text-sm text-gray-400">{tContact("about.desc")}</p>
            </header>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.name")} *
                </label>
                <input
                  required
                  value={data.name}
                  onChange={(e) => update("name", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                  placeholder={tContact("placeholders.name")}
                />
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.email")} *
                </label>
                <input
                  required
                  type="email"
                  value={data.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.company")}
                </label>
                <input
                  value={data.company}
                  onChange={(e) => update("company", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                  placeholder="ACME Inc."
                />
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.phone")}
                </label>
                <input
                  value={data.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                  placeholder="+40 712 345 678"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.country")}
                </label>
                <input
                  value={data.country}
                  onChange={(e) => update("country", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                  placeholder="Romania"
                />
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.preferred")}
                </label>
                <select
                  value={data.contactMethod}
                  onChange={(e) => update("contactMethod", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 focus:border-indigo-500 focus:outline-none"
                >
                  {["Email", "Phone", "WhatsApp"].map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </div>
            </div>

            <label className="inline-flex items-center gap-2 text-sm text-gray-300">
              <input
                type="checkbox"
                checked={data.nda}
                onChange={(e) => update("nda", e.target.checked)}
                className="size-4 rounded border-gray-600 bg-gray-900 text-indigo-500"
              />
              {tContact("fields.nda")}
            </label>

            {/* Honeypot */}
            <div className="hidden">
              <label>Website</label>
              <input
                value={data.website}
                onChange={(e) => update("website", e.target.value)}
              />
            </div>
          </div>
        )}

        {/* STEP 1: Project details */}
        {step === 1 && (
          <div className="space-y-6">
            <header className="space-y-1">
              <h2 className="text-xl font-semibold text-white">
                {tContact("project.title")}
              </h2>
              <p className="text-sm text-gray-400">
                {tContact("project.desc")}
              </p>
            </header>

            {/* Product single-select (cards) */}
            <div>
              <label className="mb-3 block text-sm text-gray-300">
                {tContact("fields.project_type")} *
              </label>
              <div className="grid gap-3 sm:grid-cols-2">
                {PRODUCT_OPTIONS.map(({ key }) => {
                  const isActive = data.product === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => selectProduct(key)}
                      className={classNames(
                        "group rounded-2xl border px-4 py-3 text-left transition",
                        isActive
                          ? "border-indigo-400 bg-indigo-500/10"
                          : "border-gray-700 bg-gray-900/40 hover:border-gray-600"
                      )}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div
                            className={classNames(
                              "text-sm font-medium",
                              isActive ? "text-indigo-200" : "text-gray-200"
                            )}
                          >
                            {productLabel(key)}
                          </div>
                          <p className="mt-1 text-xs text-gray-400">
                            {productBlurb(key)}
                          </p>
                        </div>
                        <div
                          className={classNames(
                            "mt-0.5 grid size-5 place-items-center rounded-full border",
                            isActive
                              ? "border-indigo-400 bg-indigo-500/20"
                              : "border-gray-700"
                          )}
                          aria-hidden
                        >
                          {isActive ? "●" : ""}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stage & Style */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.stage")}
                </label>
                <select
                  value={data.stage}
                  onChange={(e) => update("stage", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 focus:border-indigo-500 focus:outline-none"
                >
                  {STAGES.map((s) => (
                    <option key={s} value={s}>
                      {tContact(`stages.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.style")}
                </label>
                <select
                  value={data.style}
                  onChange={(e) => update("style", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 focus:border-indigo-500 focus:outline-none"
                >
                  {STYLE_PRESETS.map((s) => (
                    <option key={s} value={s}>
                      {tContact(`styles.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Goals */}
            <div>
              <label className="text-sm text-gray-300">
                {tContact("fields.goals")} *
              </label>
              <textarea
                value={data.goals}
                onChange={(e) => update("goals", e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                placeholder={tContact("placeholders.goals")}
              />
            </div>

            {/* Recommended features for chosen product */}
            {!!recommendedFeatures.length && (
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <div className="text-sm text-gray-300">
                    {tContact("fields.common_features")} —{" "}
                    {productLabel(data.product)}
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => resetToRecommended(data.product)}
                      className="rounded-full border border-indigo-400/50 px-3 py-1 text-xs text-indigo-200 hover:bg-indigo-500/10"
                      title="Reset to recommended"
                    >
                      {tContact("cta.reset_recommended", "Reset")}
                    </button>
                    <button
                      type="button"
                      onClick={clearAllFeatures}
                      className="rounded-full border border-gray-600 px-3 py-1 text-xs text-gray-300 hover:bg-gray-800"
                      title="Clear all"
                    >
                      {tContact("cta.clear_all", "Clear")}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {recommendedFeatures.map((f) => {
                    const selected = data.features.includes(f);
                    return (
                      <button
                        key={f}
                        type="button"
                        onClick={() => toggleList("features", f)}
                        className={classNames(
                          "rounded-full border px-3 py-1.5 text-xs",
                          selected
                            ? "border-indigo-400 bg-indigo-500/10 text-indigo-200"
                            : "border-gray-700 bg-gray-900/40 text-gray-300 hover:border-gray-600"
                        )}
                      >
                        {tContact(`features.${f}`)}
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  {tContact("placeholders.features")}
                </p>
              </div>
            )}

            {/* Extra features */}
            <div>
              <label className="mb-2 block text-sm text-gray-300">
                {tContact("fields.features")}
              </label>
              <div className="flex flex-wrap gap-2">
                {FEATURES.map((f) => {
                  const selected = data.features.includes(f);
                  return (
                    <button
                      key={f}
                      type="button"
                      onClick={() => toggleList("features", f)}
                      className={classNames(
                        "rounded-full border px-3 py-1.5 text-xs",
                        selected
                          ? "border-indigo-400 bg-indigo-500/10 text-indigo-200"
                          : "border-gray-700 bg-gray-900/40 text-gray-300 hover:border-gray-600"
                      )}
                    >
                      {tContact(`features.${f}`)}
                    </button>
                  );
                })}
              </div>
            </div>

            <TagInput
              label={tContact("fields.integrations")}
              values={data.integrations}
              setValues={(v) => update("integrations", v)}
              placeholder={tContact("placeholders.integrations")}
              name="integrations"
            />

            <TagInput
              label={tContact("fields.inspiration")}
              values={data.inspiration}
              setValues={(v) => update("inspiration", v)}
              placeholder={tContact("placeholders.inspiration")}
              name="inspiration"
            />

            <div>
              <label className="text-sm text-gray-300">
                {tContact("fields.brand_notes")}
              </label>
              <input
                value={data.colorNotes}
                onChange={(e) => update("colorNotes", e.target.value)}
                className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                placeholder={tContact("placeholders.brand_notes")}
              />
            </div>
          </div>
        )}

        {/* STEP 2: Budget & timeline */}
        {step === 2 && (
          <div className="space-y-6">
            <header className="space-y-1">
              <h2 className="text-xl font-semibold text-white">
                {tContact("budget.title")}
              </h2>
              <p className="text-sm text-gray-400">{tContact("budget.desc")}</p>
            </header>

            <div className="space-y-2">
              <label className="text-sm text-gray-300">
                {tContact("fields.budget")}
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min={BUDGET_MIN}
                  max={BUDGET_MAX}
                  step={BUDGET_STEP}
                  value={data.budget}
                  onChange={(e) => update("budget", Number(e.target.value))}
                  className="w-full accent-indigo-500"
                  disabled={data.unsureBudget}
                />
                <div className="w-40 text-right text-sm tabular-nums text-indigo-200">
                  {data.unsureBudget
                    ? tContact("labels.not_sure")
                    : `${formatMoneyEUR(data.budget)}`}
                </div>
              </div>
              <label className="mt-1 inline-flex items-center gap-2 text-xs text-gray-400">
                <input
                  type="checkbox"
                  className="size-4 rounded border-gray-600 bg-gray-900 text-indigo-500"
                  checked={data.unsureBudget}
                  onChange={(e) => update("unsureBudget", e.target.checked)}
                />
                {tContact("labels.help_scope")}
              </label>
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.start")}
                </label>
                <select
                  value={data.startWhen}
                  onChange={(e) => update("startWhen", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 focus:border-indigo-500 focus:outline-none"
                >
                  {[
                    "asap",
                    "within_1_2_weeks",
                    "within_1_month",
                    "one_three_months",
                    "flexible",
                  ].map((s) => (
                    <option key={s} value={s}>
                      {tContact(`starts.${s}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-300">
                  {tContact("fields.deadline")}
                </label>
                <input
                  type="date"
                  value={data.deadline}
                  onChange={(e) => update("deadline", e.target.value)}
                  className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 focus:border-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            <div>
              <label className="text-sm text-gray-300">
                {tContact("fields.notes")}
              </label>
              <textarea
                value={data.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={4}
                className="mt-1 w-full rounded-2xl border border-gray-700 bg-gray-950/50 px-3 py-2 text-sm text-gray-100 placeholder:text-gray-500 focus:border-indigo-500 focus:outline-none"
                placeholder={tContact("placeholders.notes")}
              />
            </div>
          </div>
        )}

        <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {error ? <p className="text-sm text-rose-300">{error}</p> : <span />}
          <div className="flex gap-3">
            {step > 0 && (
              <button
                type="button"
                onClick={prev}
                className="rounded-xl border border-gray-700 px-4 py-2 text-sm text-gray-200 hover:bg-gray-800"
              >
                {tContact("cta.back")}
              </button>
            )}
            {step < 2 && (
              <button
                type="button"
                onClick={() => validateStep() && next()}
                className="rounded-xl bg-indigo-500 px-4 py-2 text-sm font-medium text-indigo-950 hover:bg-indigo-400"
              >
                {tContact("cta.continue")}
              </button>
            )}
            {step === 2 && (
              <button
                type="submit"
                disabled={busy}
                className={classNames(
                  "rounded-xl px-4 py-2 text-sm font-medium",
                  busy
                    ? "cursor-wait bg-indigo-800/50 text-indigo-200"
                    : "bg-indigo-500 text-indigo-950 hover:bg-indigo-400"
                )}
              >
                {busy ? tContact("cta.sending") : tContact("cta.send")}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sidebar summary */}
      <aside className="mx-auto mt-6 grid gap-3 sm:grid-cols-2">
        <div className="rounded-2xl border border-gray-800 bg-gray-900/30 p-4">
          <h3 className="mb-2 text-sm font-semibold text-white">
            {tContact("summary.you")}
          </h3>
          <ul className="space-y-1 text-xs text-gray-300">
            <li>
              <span className="text-gray-500">{tContact("fields.name")}:</span>{" "}
              {data.name || "—"}
            </li>
            <li>
              <span className="text-gray-500">{tContact("fields.email")}:</span>{" "}
              {data.email || "—"}
            </li>
            <li>
              <span className="text-gray-500">
                {tContact("fields.company")}:
              </span>{" "}
              {data.company || "—"}
            </li>
            <li>
              <span className="text-gray-500">
                {tContact("labels.contact")}:
              </span>{" "}
              {data.contactMethod}
            </li>
            <li>
              <span className="text-gray-500">NDA:</span>{" "}
              {data.nda ? tContact("labels.yes") : tContact("labels.no")}
            </li>
          </ul>
        </div>
        <div className="rounded-2xl border border-gray-800 bg-gray-900/30 p-4">
          <h3 className="mb-2 text-sm font-semibold text-white">
            {tContact("summary.project")}
          </h3>
          <ul className="space-y-1 text-xs text-gray-300">
            <li>
              <span className="text-gray-500">
                {tContact("fields.project_type")}:
              </span>{" "}
              {data.product ? productLabel(data.product) : "—"}
            </li>
            <li>
              <span className="text-gray-500">{tContact("fields.stage")}:</span>{" "}
              {tContact(`stages.${data.stage}`)}
            </li>
            <li>
              <span className="text-gray-500">{tContact("fields.style")}:</span>{" "}
              {tContact(`styles.${data.style}`)}
            </li>
            <li>
              <span className="text-gray-500">
                {tContact("fields.budget")}:
              </span>{" "}
              {data.unsureBudget
                ? tContact("labels.not_sure")
                : formatMoneyEUR(data.budget)}
            </li>
            <li>
              <span className="text-gray-500">{tContact("fields.start")}:</span>{" "}
              {tContact(`starts.${data.startWhen}`)}
            </li>
          </ul>
        </div>
      </aside>
    </form>
  );
}
